import { memo, useEffect, useRef, useState } from 'react';
import { Handle, NodeResizer, Position, type NodeProps } from '@xyflow/react';
import { ShapeRenderer } from './ShapeRenderer';
import { SHAPE_META } from './shapeMeta';
import { ICON_MAP } from './iconLibrary';
import { ImagePlus } from 'lucide-react';
import { figureCatalogByKey } from '@/components/organograma/organogramaFigureCatalog';
import { cn } from '@/lib/utils';
import type { FluxogramaNode as ApiNode, FluxogramaShape } from '@/types/fluxograma';

export interface ShapeNodeData {
  label: string;
  shape: FluxogramaShape;
  width: number;
  height: number;
  style: ApiNode['style'];
  descricao?: string | null;
  responsavelLabel?: string | null;
  departamentoLabel?: string | null;
  projetoLabel?: string | null;
  apiNode?: ApiNode;
  /** Callback que atualiza o label no estado externo */
  onLabelChange?: (id: string, newLabel: string) => void;
  /** Avisa que a edição inline terminou, para o clique que a encerrou não fechar o painel */
  onEditEnd?: (id: string) => void;
  readonly?: boolean;
  /** Index signature exigida pelo @xyflow/react v12 (Node['data'] extends Record<string, unknown>) */
  [key: string]: unknown;
}

function ShapeNodeImpl({ id, data, selected }: NodeProps) {
  const d = data as ShapeNodeData;
  // Defesa: shape pode estar momentaneamente inválido durante rehidratação/undo
  const meta = SHAPE_META[d.shape] ?? SHAPE_META.process;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(d.label ?? '');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setDraft(d.label);
  }, [d.label]);

  useEffect(() => {
    if (editing) {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
    }
  }, [editing]);

  // `source` distingue blur (o clique que tira o foco pode cair no canvas vazio, o que
  // o React Flow trata como deselect) de Enter (nenhum clique envolvido — não há risco
  // de desmarcar nada, então não precisa avisar `onEditEnd` para proteger a seleção).
  const commit = (source: 'blur' | 'enter') => {
    setEditing(false);
    if (draft !== d.label) d.onLabelChange?.(id, draft);
    if (source === 'blur') d.onEditEnd?.(id);
  };

  const bgColor = d.style?.bg_color || lighten(meta.defaultColor, 0.85);
  // Figura não tem fundo: sem cor escolhida, ícone e legenda seguem a cor de texto do tema (legível no claro e no escuro).
  const ehFigura = d.shape === 'figura';
  const borderColor = d.style?.border_color || (ehFigura ? 'hsl(var(--foreground))' : meta.defaultColor);
  const textColor = d.style?.text_color || (ehFigura ? 'hsl(var(--foreground))' : '#0F172A');
  const borderWidth = d.style?.border_width ?? 2;
  const fontSize = d.style?.font_size ?? 13;
  const IconComponent = d.style?.icon ? ICON_MAP[d.style.icon] : undefined;
  const defaultAlign = d.shape === 'annotation' || d.shape === 'container' ? 'left' : 'center';
  const textAlign = d.style?.text_align || defaultAlign;
  const justifyClass = textAlign === 'left' ? 'justify-start' : textAlign === 'right' ? 'justify-end' : 'justify-center';
  const textAlignClass = textAlign === 'left' ? 'text-left' : textAlign === 'right' ? 'text-right' : 'text-center';
  const textValign = d.style?.text_valign || 'middle';
  const alignItemsClass = textValign === 'top' ? 'items-start' : textValign === 'bottom' ? 'items-end' : 'items-center';

  // Handles revelados no hover do nó ou quando selecionado (menos poluição visual).
  const handleVisibility = selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100';

  // Caixa real do handle é minúscula (a aresta cola na borda do nó, sem gap).
  // O ponto visível (::after, 12px) e a área de clique ampla (::before, 24px)
  // não entram no getBoundingClientRect do handle, então não afastam a linha.
  const sourceHandleClass =
    '!w-px !h-px !min-w-0 !min-h-0 !border-0 !bg-transparent ' +
    "after:content-[''] after:pointer-events-none after:absolute after:left-1/2 after:top-1/2 " +
    'after:h-3 after:w-3 after:-translate-x-1/2 after:-translate-y-1/2 after:rounded-full ' +
    'after:bg-primary after:border-2 after:border-background after:transition-transform ' +
    "before:content-[''] before:absolute before:left-1/2 before:top-1/2 before:h-6 before:w-6 " +
    'before:-translate-x-1/2 before:-translate-y-1/2 ' +
    `transition-opacity duration-150 hover:after:scale-125 ${handleVisibility}`;

  return (
    <div className="group relative w-full h-full" style={{ minWidth: 32, minHeight: 32 }}>
      {!d.readonly && (
        <NodeResizer minWidth={64} minHeight={32} isVisible={selected} lineClassName="!stroke-primary" handleClassName="!bg-primary" />
      )}

      {d.shape === 'figura' && (
        <div
          className="absolute inset-x-0 top-0 flex items-center justify-center pointer-events-none"
          style={{ bottom: d.label || editing ? Math.round(fontSize * 1.6) : 0 }}
        >
          <FiguraDoNo figura={d.style?.figura} cor={borderColor} readonly={d.readonly} />
        </div>
      )}

      <div className="absolute inset-0 pointer-events-none">
        <ShapeRenderer
          shape={d.shape}
          width={d.width}
          height={d.height}
          bgColor={bgColor}
          borderColor={borderColor}
          borderWidth={borderWidth}
        />
      </div>

      <div
        className={
          d.shape === 'annotation'
            ? cn(
                'absolute inset-0 flex flex-col justify-start px-3 py-2 select-none overflow-hidden',
                textAlign === 'left' ? 'items-start' : textAlign === 'right' ? 'items-end' : 'items-center',
              )
            : d.shape === 'container'
              ? cn('absolute inset-x-0 top-0 flex items-center px-3 py-1.5 select-none overflow-hidden', justifyClass)
              : d.shape === 'swimlane'
                ? 'absolute inset-y-0 left-0 w-9 flex items-center justify-center select-none overflow-hidden'
                : d.shape === 'figura'
                  // Figura: legenda opcional embaixo; o resto do nó continua recebendo o duplo clique.
                  ? cn('absolute inset-0 flex items-end select-none', justifyClass, textAlignClass)
                  : cn('absolute inset-0 flex px-3 select-none', alignItemsClass, justifyClass, textAlignClass)
        }
        style={{ color: textColor, fontSize, lineHeight: 1.25 }}
        onDoubleClick={(e) => {
          if (d.readonly) return;
          e.stopPropagation();
          setEditing(true);
        }}
      >
        {editing ? (
          <textarea
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => commit('blur')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                commit('enter');
              } else if (e.key === 'Escape') {
                setEditing(false);
                setDraft(d.label);
              }
            }}
            className={
              d.shape === 'annotation'
                ? cn('w-full h-full resize-none bg-transparent outline-none', textAlignClass)
                : d.shape === 'container'
                  ? cn('w-full resize-none bg-transparent outline-none', textAlignClass)
                  : d.shape === 'swimlane'
                    ? 'w-24 resize-none bg-transparent text-center outline-none -rotate-90'
                    : d.shape === 'figura'
                      ? cn('w-full resize-none bg-transparent outline-none', textAlignClass)
                      : cn('w-[85%] resize-none bg-transparent outline-none', textAlignClass)
            }
            rows={d.shape === 'annotation' ? 4 : d.shape === 'container' || d.shape === 'swimlane' || d.shape === 'figura' ? 1 : 2}
            style={{ color: textColor, fontSize }}
          />
        ) : d.shape === 'annotation' ? (
          <div className="w-full h-full overflow-hidden">
            <div className={cn('font-semibold break-words whitespace-pre-wrap', textAlignClass)}>
              {d.label || meta.label}
            </div>
            {d.descricao && (
              <div
                className={cn('mt-1 break-words whitespace-pre-wrap opacity-80', textAlignClass)}
                style={{ fontSize: Math.max(11, fontSize - 1) }}
              >
                {d.descricao}
              </div>
            )}
          </div>
        ) : d.shape === 'container' ? (
          <span className={cn('font-semibold break-words uppercase tracking-wide', textAlignClass)} style={{ fontSize: Math.max(11, fontSize - 1) }}>
            {d.label || meta.label}
          </span>
        ) : d.shape === 'figura' ? (
          // Sem legenda, a figura fica sozinha (não mostra "Figura" como texto padrão).
          d.label ? <span className="w-full truncate font-medium">{d.label}</span> : null
        ) : d.shape === 'swimlane' ? (
          <span
            className="font-semibold uppercase tracking-wide whitespace-nowrap -rotate-90 origin-center inline-block"
            style={{ fontSize: Math.max(11, fontSize - 1) }}
          >
            {d.label || meta.label}
          </span>
        ) : (
          <div className="flex flex-col items-center gap-1">
            {d.style?.image_url ? (
              <img
                src={d.style.image_url}
                alt=""
                referrerPolicy="no-referrer"
                loading="lazy"
                className="h-7 w-7 rounded object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              IconComponent && <IconComponent size={Math.max(16, fontSize + 4)} style={{ color: textColor }} />
            )}
            <span className="font-medium break-words whitespace-pre-wrap">{d.label || meta.label}</span>
          </div>
        )}
      </div>

      {(d.responsavelLabel || d.departamentoLabel || d.projetoLabel) && !editing && (
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 flex gap-1 max-w-[90%]">
          {d.responsavelLabel && (
            <span className="px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] truncate max-w-[120px]" title={d.responsavelLabel}>
              {d.responsavelLabel}
            </span>
          )}
          {d.departamentoLabel && (
            <span className="px-1.5 py-0.5 rounded-full bg-slate-700 text-white text-[10px] truncate max-w-[120px]" title={d.departamentoLabel}>
              {d.departamentoLabel}
            </span>
          )}
          {d.projetoLabel && (
            <span className="px-1.5 py-0.5 rounded-full bg-violet-700 text-white text-[10px] truncate max-w-[120px]" title={d.projetoLabel}>
              {d.projetoLabel}
            </span>
          )}
        </div>
      )}

      {/* 4 handles em volta — source+target em cada para permitir conexão em qualquer direção */}
      {(['top', 'right', 'bottom', 'left'] as const).map((pos) => (
        <Handle
          key={`s-${pos}`}
          type="source"
          id={pos}
          position={Position[pos.charAt(0).toUpperCase() + pos.slice(1) as 'Top' | 'Right' | 'Bottom' | 'Left']}
          className={sourceHandleClass}
          isConnectable={!d.readonly}
        />
      ))}
      {(['top', 'right', 'bottom', 'left'] as const).map((pos) => (
        <Handle
          key={`t-${pos}`}
          type="target"
          id={`t-${pos}`}
          position={Position[pos.charAt(0).toUpperCase() + pos.slice(1) as 'Top' | 'Right' | 'Bottom' | 'Left']}
          className="!w-px !h-px !min-w-0 !min-h-0 !border-0 !opacity-0"
          isConnectable={!d.readonly}
        />
      ))}
    </div>
  );
}

/** Desenho da forma `figura`: ícone do catálogo, imagem da biblioteca ou um aviso para escolher a figura. */
function FiguraDoNo({ figura, cor, readonly }: { figura?: NonNullable<ApiNode['style']>['figura']; cor: string; readonly?: boolean }) {
  if (figura?.tipo === 'biblioteca' && figura.url) {
    return <img src={figura.url} alt="" draggable={false} className="h-full w-full object-contain" />;
  }
  const Icon = figura?.tipo === 'catalogo' ? figureCatalogByKey.get(figura.chave as never)?.icon : undefined;
  if (Icon) return <Icon className="h-full w-full" style={{ color: cor }} strokeWidth={1.5} />;
  // Só leitura / link público: sem figura escolhida não mostra a instrução de edição.
  if (readonly) return null;
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-muted-foreground/40 text-muted-foreground">
      <ImagePlus className="h-1/2 w-1/2 max-h-10 max-w-10" />
      <span className="text-[10px] leading-none">Escolha a figura</span>
    </div>
  );
}

// Helper p/ clarear cor: aceita #RRGGBB e devolve mesma cor misturada com branco.
function lighten(hex: string, amount: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const num = parseInt(m[1], 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  r = Math.round(r + (255 - r) * amount);
  g = Math.round(g + (255 - g) * amount);
  b = Math.round(b + (255 - b) * amount);
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

export const ShapeNode = memo(ShapeNodeImpl);
